import { PhotoItem } from "../interfaces/PhotoItem";
import { removeAccents, removeSpaces } from "../utils/General";
import {
  Camera,
  CameraResultType,
  CameraSource,
  ImageOptions,
  Photo,
} from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import { isPlatform } from "@ionic/react";
import { useEffect } from "react";

const PHOTOS_PREF_KEY = "photos";

interface UsePhotoProps {
  photos: PhotoItem[];
  setPhotos: React.Dispatch<React.SetStateAction<PhotoItem[]>>;
  setTempFile: React.Dispatch<React.SetStateAction<PhotoItem | undefined>>;
  currentMedia: PhotoItem[];
  setShowDuplicateInCurrentMediaToast: React.Dispatch<
    React.SetStateAction<boolean>
  >;
}

export const usePhoto = ({
  photos,
  setPhotos,
  setTempFile,
  currentMedia,
  setShowDuplicateInCurrentMediaToast,
}: UsePhotoProps) => {
  useEffect(() => {
    const loadSaved = async () => {
      const { value } = await Preferences.get({ key: PHOTOS_PREF_KEY });
      const photosInPref: PhotoItem[] = value ? JSON.parse(value) : [];

      if (!isPlatform("hybrid")) {
        for (let photo of photosInPref) {
          try {
            const fileExists = await Filesystem.stat({
              path: photo.fileName,
              directory: Directory.Data,
            });

            if (fileExists && fileExists.type === "file") {
              const file = await Filesystem.readFile({
                path: photo.fileName,
                directory: Directory.Data,
              });
              photo.webviewPath = `data:image/jpeg;base64,${file.data}`;
            } else {
              console.error(`El archivo ${photo.fileName} no existe.`);
            }
          } catch (error) {
            console.error(
              `Error al verificar el archivo ${photo.fileName}:`,
              error
            );
          }
        }
      }
      setPhotos(photosInPref);
    };

    loadSaved();
  }, []);

  useEffect(() => {
    if (photos.length > 0) {
      Preferences.set({ key: PHOTOS_PREF_KEY, value: JSON.stringify(photos) });
    }
  }, [photos]);

  const requestPermissions = async () => {
    if (!Capacitor.isNativePlatform()) return;

    const status = await Camera.checkPermissions();
    if (status.camera !== "granted") {
      const request = await Camera.requestPermissions();
      if (request.camera !== "granted") {
        throw new Error("Permiso de cámara denegado");
      }
    }
  };

  const takePhoto = async (
    source: string, // string para compatibilidad con context
    tag: { type: string; detail: string },
    options: Partial<ImageOptions> = {}
  ) => {
    try {
      await requestPermissions();

      const typedSource = source.toUpperCase() as
        | "CAMERA"
        | "GALLERY"
        | "PROMPT";

      const sourceMap = {
        CAMERA: CameraSource.Camera,
        GALLERY: CameraSource.Photos,
        PROMPT: CameraSource.Prompt,
      };

      const photo = await Camera.getPhoto({
        quality: 60,
        resultType: CameraResultType.DataUrl,
        source: sourceMap[typedSource],
        promptLabelPicture: "Tomar una foto nueva",
        promptLabelPhoto: "Abrir la galería",
        allowEditing: false,
        ...options,
      });

      if (!photo) {
        console.log("El usuario canceló la acción de tomar la foto.");
        return;
      }

      const generateUniqueFileName = () => {
        let formattedFileName = removeSpaces(
          removeAccents(tag.detail)
        ).toLowerCase();
        let baseFileName = formattedFileName;
        let fileName = `${formattedFileName}_img.jpeg`;
        let fileNumber = 1;

        while (currentMedia.some((media) => media.fileName === fileName)) {
          formattedFileName = `${baseFileName}${fileNumber}`;
          fileName = `${formattedFileName}_img.jpeg`;
          fileNumber++;
        }

        return fileName;
      };

      const fileName = generateUniqueFileName();
      const savedImage: PhotoItem | undefined = await savePhoto(
        photo,
        fileName,
        tag
      );
      savedImage && setTempFile(savedImage);
    } catch (error) {
      console.error(
        "Error al tomar la foto: ",
        (error as any)?.message || error
      );
    }
  };

  const savePhoto = async (
    photo: Photo,
    fileName: string,
    tag: { type: string; detail: string }
  ): Promise<PhotoItem | undefined> => {
    let base64data: string;

    if (isPlatform("hybrid")) {
      const file = await Filesystem.readFile({ path: photo.path! });
      base64data = file.data as string;
    } else {
      // DataUrl -> extraemos solo la parte base64
      base64data = photo.dataUrl!.split(",")[1];
    }

    const id = idFromBase64(base64data);
    const formattedTag = { ...tag, type: removeAccents(tag.type) };

    const savedFile = await Filesystem.writeFile({
      path: fileName,
      directory: Directory.Data,
      data: base64data,
    });

    // Salida igual que la versión anterior
    if (isPlatform("hybrid")) {
      return {
        id,
        fileName: savedFile.uri,
        webviewPath: Capacitor.convertFileSrc(savedFile.uri),
        tag: formattedTag,
      };
    }

    return {
      id,
      fileName,
      webviewPath: `data:image/jpeg;base64,${base64data}`,
      tag: formattedTag,
    };
  };

  const crypto = require("crypto");
  const idFromBase64 = (base64String: string): string => {
    const buffer = Buffer.from(base64String, "base64");
    const hash = crypto.createHash("sha256").update(buffer).digest("base64");
    return hash
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "")
      .substring(0, 10);
  };

  const deletePhoto = async (fileName: string) => {
    setPhotos(photos.filter((photo) => photo.fileName !== fileName));
    await Filesystem.deleteFile({
      path: fileName,
      directory: Directory.Data,
    });
  };

  const clearPhotos = async () => {
    setPhotos([]);
    await Preferences.remove({ key: PHOTOS_PREF_KEY });
  };

  return {
    takePhoto,
    deletePhoto,
    clearPhotos,
  };
};
